
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Quote } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Tables } from "@/integrations/supabase/types";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";

type Testimonial = Tables<"testimonials">;
type ServiceCategory = "Consulting" | "Training" | "Coaching";

interface PublicTestimonialsProps {
  serviceCategory?: ServiceCategory;
  title?: string;
  subtitle?: string;
  limit?: number;
  showAll?: boolean;
  previewMode?: boolean;
}

const TestimonialCard = ({ testimonial, isPreview }: { testimonial: Testimonial; isPreview?: boolean }) => {
  return (
    <Card className="h-full flex flex-col bg-white border border-slate-200 shadow-sm">
      <CardContent className="flex flex-col h-full pt-6">
        <div className="mb-4 text-slate-300">
          <Quote size={36} />
        </div>
        <p className="text-slate-700 flex-grow mb-6">
          {testimonial.content.length > 180 
            ? `${testimonial.content.substring(0, 180)}...` 
            : testimonial.content}
        </p>
        <div className="mt-auto">
          <p className="text-sm font-medium text-slate-900">
            {testimonial.role_title}
            {isPreview && !testimonial.approved && (
              <Badge variant="outline" className="ml-2 bg-yellow-100 text-yellow-800 border-yellow-200">
                Pending
              </Badge>
            )}
          </p>
          <p className="text-xs text-slate-500">{testimonial.industry}</p>
        </div>
      </CardContent>
    </Card>
  );
};

const PublicTestimonials = ({
  serviceCategory,
  title = "What Our Clients Say",
  subtitle = "Trusted by professionals across various industries",
  limit = 6,
  showAll = false,
  previewMode = false
}: PublicTestimonialsProps) => {
  // Fetch testimonials, filtering by approval status unless in admin preview mode
  const { data: testimonials, isLoading, error } = useQuery({
    queryKey: ["public-testimonials", serviceCategory, showAll, previewMode],
    queryFn: async () => {
      let query = supabase
        .from("testimonials")
        .select("id, content, role_title, industry, service_category, approved")
        .limit(limit);
      
      // Only filter for approved testimonials if not in "show all" mode
      if (!showAll) {
        query = query.eq("approved", true);
      }
      
      // Add service category filter if provided
      if (serviceCategory) {
        query = query.eq("service_category", serviceCategory);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      return data as Testimonial[];
    },
  });

  if (error) {
    console.error("Error loading testimonials:", error);
    return null; // Don't show section if there's an error
  }

  return (
    <section className={`py-16 ${previewMode ? "" : "bg-slate-50"}`}>
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            {title}
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            {subtitle}
          </p>
          {serviceCategory && (
            <div className="mt-2">
              <span className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-sm font-medium text-blue-800">
                {serviceCategory}
              </span>
            </div>
          )}
          {previewMode && showAll && (
            <div className="mt-2">
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-100">
                Admin Preview Mode - Showing All Testimonials
              </Badge>
            </div>
          )}
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="h-64">
                <CardContent className="pt-6">
                  <Skeleton className="h-8 w-8 mb-4" />
                  <Skeleton className="h-24 w-full mb-4" />
                  <Skeleton className="h-4 w-1/2 mb-2" />
                  <Skeleton className="h-3 w-1/3" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : testimonials && testimonials.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((testimonial) => (
              <TestimonialCard 
                key={testimonial.id} 
                testimonial={testimonial} 
                isPreview={previewMode && showAll}
              />
            ))}
          </div>
        ) : (
          <p className="text-center text-slate-500">
            {serviceCategory 
              ? `No ${serviceCategory.toLowerCase()} testimonials available at the moment.` 
              : "No testimonials available at the moment."}
          </p>
        )}
      </div>
    </section>
  );
};

export default PublicTestimonials;
